import React from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Clock, TrendingUp, Lightbulb, Shield, Users, Zap } from 'lucide-react';

import {  useState } from 'react';
import posthog from 'posthog-js';


import { DialogForm } from '../ui/DialogForm';
import { Helmet } from 'react-helmet-async'; // Import Helmet for managing meta tags


const Benefits = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [variant, setVariant] = useState('control');
  
  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.target);
      const response = await fetch('https://formspree.io/f/xovvpnyb', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setShowThankYou(true);
        e.target.reset();
        // Track successful demo request
        posthog.capture('demo_request', {
          variant: variant
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = (buttonType) => {
    posthog.capture('button_click', {
      button_type: buttonType
    });
  
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        'event_category': 'engagement',
        'event_label': buttonType
      });
    }
  };
  const benefits = [
    {
      icon: DollarSign,
      title: t('benefits.significant_cost_savings.title'),
      description: t('benefits.significant_cost_savings.description'),
      stats: [t('benefits.significant_cost_savings.stats.0'), t('benefits.significant_cost_savings.stats.1')],
      color: 'violet'
    },
    {
      icon: Clock,
      title: t('benefits.lightning_fast_response.title'),
      description: t('benefits.lightning_fast_response.description'),
      stats: [t('benefits.lightning_fast_response.stats.0'), t('benefits.lightning_fast_response.stats.1')],
      color: 'indigo'
    },
    {
      icon: TrendingUp,
      title: t('benefits.revenue_growth.title'),
      description: t('benefits.revenue_growth.description'),
      stats: [t('benefits.revenue_growth.stats.0'), t('benefits.revenue_growth.stats.1')],
      color: 'violet'
    },
    {
      icon: Lightbulb,
      title: t('benefits.effortless_setup.title'),
      description: t('benefits.effortless_setup.description'),
      stats: [t('benefits.effortless_setup.stats.0'), t('benefits.effortless_setup.stats.1')],
      color: 'indigo'
    }
  ];

  const additionalBenefits = [
    {
      icon: Shield,
      title: t('benefits.enterprise_grade_security.title'),
      description: t('benefits.enterprise_grade_security.description')
    },
    {
      icon: Users,
      title: t('benefits.scalable_solution.title'),
      description: t('benefits.scalable_solution.description')
    },
    {
      icon: Zap,
      title: t('benefits.always_improving.title'),
      description: t('benefits.always_improving.description')
    }
  ];

  return (
    <section className="py-6 relative overflow-hidden">

      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 
            bg-clip-text text-transparent mb-6">
            {t('benefits.benefits_heading')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('benefits.benefits_description')}
          </p>
        </div>

        {/* Main Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="relative group">
              {/* Gradient border effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-indigo-500/20 
                rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative h-full bg-white rounded-xl p-6 shadow-lg hover:shadow-xl 
                transition-all duration-300 border border-gray-100">
                {/* Icon */}
                <div className={`p-3 rounded-lg bg-${benefit.color}-500/10 w-fit mb-4`}>
                  <benefit.icon className={`w-6 h-6 text-${benefit.color}-600`} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-gray-600 mb-4">{benefit.description}</p>

                {/* Stats */}
                <div className="space-y-2 mb-4">
                  {benefit.stats.map((stat, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mr-2" />
                      {stat}
                    </div>
                  ))}
                </div>

              </div>
            </div>
          ))}
        </div>

        {/* Additional Benefits */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-violet-500/10 to-indigo-500/10">
                    <benefit.icon className="w-6 h-6 text-violet-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-8 text-center flex justify-center gap-4">
          <DialogForm
            triggerText={t('App.book_demo')}
            title={t('App.book_demo')}
            description="Schedule a personalized demo with our team"
            onSubmit={handleDemoSubmit}
            onClick={() => handleButtonClick('demo_button')}

          />
          <DialogForm
            triggerText={t('App.get_free_trial')}
            title={t('App.get_free_trial')}
            description="Start your free trial today"
            onSubmit={handleDemoSubmit}
            onClick={() => handleButtonClick('trial_button')}

            buttonStyle="black"
          />
      </div>



        
      </div>
    </section>
  );
};

export default Benefits;
